<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Policy from 'dashboard/components/policy.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  question: {
    type: String,
    required: true,
  },
  answer: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: 'approved',
  },
  embeddingStatus: {
    type: String,
    default: 'processing',
  },
  enabled: {
    type: Boolean,
    default: true,
  },
  documentable: {
    type: Object,
    default: null,
  },
  createdVia: {
    type: String,
    default: 'manual',
  },
  actionUrl: {
    type: String,
    default: null,
  },
  mediaUrl: {
    type: String,
    default: null,
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  updatedAt: {
    type: Number,
    required: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  showMenu: {
    type: Boolean,
    default: true,
  },
  showActions: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['action', 'navigate', 'select', 'hover']);

const { t } = useI18n();
const { relativeTime } = useLocalizedTime();

const CREATED_VIA_CONFIG = {
  manual: { color: 'slate', labelKey: 'CAPTAIN.RESPONSES.CREATED_VIA.MANUAL' },
  document: {
    color: 'blue',
    labelKey: 'CAPTAIN.RESPONSES.CREATED_VIA.DOCUMENT',
  },
  conversation: {
    color: 'iris',
    labelKey: 'CAPTAIN.RESPONSES.CREATED_VIA.CONVERSATION',
  },
  url: {
    color: 'teal',
    labelKey: 'CAPTAIN.RESPONSES.CREATED_VIA.URL',
  },
};

const [showActionsDropdown, toggleDropdown] = useToggle();

const modelValue = computed({
  get: () => props.isSelected,
  set: () => emit('select', props.id),
});

const statusAction = computed(() => {
  if (props.status === 'pending') {
    return [
      {
        label: t('CAPTAIN.RESPONSES.OPTIONS.APPROVE'),
        value: 'approve',
        action: 'approve',
        icon: 'i-lucide-circle-check-big',
      },
    ];
  }
  return [];
});

const pauseAction = computed(() => [
  {
    label: t(
      props.enabled
        ? 'CAPTAIN.RESPONSES.OPTIONS.PAUSE'
        : 'CAPTAIN.RESPONSES.OPTIONS.REACTIVATE'
    ),
    value: props.enabled ? 'pause' : 'reactivate',
    action: props.enabled ? 'pause' : 'reactivate',
    icon: props.enabled ? 'i-lucide-pause' : 'i-lucide-play',
  },
]);

const menuItems = computed(() => [
  ...statusAction.value,
  ...pauseAction.value,
  {
    label: t('CAPTAIN.RESPONSES.OPTIONS.EDIT_RESPONSE'),
    value: 'edit',
    action: 'edit',
    icon: 'i-lucide-pencil-line',
  },
  {
    label: t('CAPTAIN.RESPONSES.OPTIONS.DELETE_RESPONSE'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const timestamp = computed(() =>
  relativeTime(props.updatedAt || props.createdAt)
);

const handleAssistantAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};

const handleDocumentableClick = () => {
  emit('navigate', {
    id: props.documentable.id,
    type: props.documentable.type,
  });
};

const embeddingStatusLabel = computed(() => {
  if (props.embeddingStatus !== 'failed' && !props.enabled) {
    return t('CAPTAIN.RESPONSES.EMBEDDING_STATUS.PAUSED');
  }
  return t(
    `CAPTAIN.RESPONSES.EMBEDDING_STATUS.${props.embeddingStatus.toUpperCase()}`
  );
});

const embeddingStatusDotClass = computed(() => {
  if (props.embeddingStatus === 'failed') return 'bg-n-ruby-9';
  if (!props.enabled) return 'bg-n-slate-9';
  if (props.embeddingStatus === 'ready') return 'bg-n-teal-9';
  return 'bg-n-amber-9';
});

const createdViaConfig = computed(
  () => CREATED_VIA_CONFIG[props.createdVia] || CREATED_VIA_CONFIG.manual
);

const hasShareableContent = computed(
  () => !!(props.actionUrl || props.mediaUrl || props.attachments.length)
);

const shareableContentTooltip = computed(() => {
  if (props.attachments.length) {
    return t('CAPTAIN.RESPONSES.SHAREABLE_CONTENT.ATTACHMENT_TOOLTIP', {
      filename: props.attachments.map(file => file.filename).join(', '),
    });
  }
  return t('CAPTAIN.RESPONSES.SHAREABLE_CONTENT.URL_TOOLTIP');
});
</script>

<template>
  <CardLayout
    selectable
    class="relative"
    :class="{ 'rounded-md': compact }"
    @mouseenter="emit('hover', true)"
    @mouseleave="emit('hover', false)"
  >
    <div v-show="selectable" class="absolute top-7 ltr:left-3 rtl:right-3">
      <Checkbox v-model="modelValue" />
    </div>
    <div class="flex relative justify-between w-full gap-1">
      <span class="text-base text-n-slate-12 line-clamp-1">
        {{ question }}
      </span>
      <div v-if="!compact && showMenu" class="flex items-center gap-2">
        <Policy
          v-on-clickaway="() => toggleDropdown(false)"
          :permissions="['administrator']"
          class="relative flex items-center group"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="mt-1 ltr:right-0 rtl:right-0 top-full"
            @action="handleAssistantAction($event)"
          />
        </Policy>
      </div>
    </div>
    <span class="text-n-slate-11 text-sm line-clamp-5">
      {{ answer }}
    </span>
    <div
      v-if="!compact"
      class="flex items-start justify-between flex-col-reverse md:flex-row gap-3"
    >
      <Policy v-if="showActions" :permissions="['administrator']">
        <div class="flex items-center gap-2 sm:gap-5 w-full">
          <Button
            v-if="status === 'pending'"
            :label="$t('CAPTAIN.RESPONSES.OPTIONS.APPROVE')"
            icon="i-lucide-circle-check-big"
            sm
            link
            class="hover:!no-underline"
            @click="
              handleAssistantAction({ action: 'approve', value: 'approve' })
            "
          />
          <Button
            :label="$t('CAPTAIN.RESPONSES.OPTIONS.EDIT_RESPONSE')"
            icon="i-lucide-pencil-line"
            sm
            slate
            link
            class="hover:!no-underline"
            @click="
              handleAssistantAction({
                action: 'edit',
                value: 'edit',
              })
            "
          />
          <Button
            :label="$t('CAPTAIN.RESPONSES.OPTIONS.DELETE_RESPONSE')"
            icon="i-lucide-trash"
            sm
            ruby
            link
            class="hover:!no-underline"
            @click="
              handleAssistantAction({ action: 'delete', value: 'delete' })
            "
          />
        </div>
      </Policy>
      <div
        class="flex items-center gap-3"
        :class="{ 'justify-between w-full': !showActions }"
      >
        <div class="inline-flex items-center gap-3 min-w-0">
          <Label
            :label="$t(createdViaConfig.labelKey)"
            :color="createdViaConfig.color"
            compact
          />
          <span
            v-if="hasShareableContent"
            :title="shareableContentTooltip"
            class="shrink-0"
          >
            <Icon
              :icon="
                attachments.length ? 'i-lucide-paperclip' : 'i-lucide-link'
              "
              class="size-3.5 text-n-slate-11"
            />
          </span>
          <div
            v-if="documentable"
            class="text-sm text-n-slate-11 grid grid-cols-[auto_1fr] items-center gap-1 min-w-0"
          >
            <Icon
              v-if="documentable.type === 'Captain::Document'"
              icon="i-ph-files-light"
              class="size-3.5"
            />
            <Icon
              v-else-if="documentable.type === 'User'"
              icon="i-ph-user-circle-plus"
              class="size-3.5"
            />
            <Icon
              v-else-if="documentable.type === 'Conversation'"
              icon="i-ph-chat-circle-dots"
              class="size-3.5"
            />
            <span
              v-if="documentable.type === 'Captain::Document'"
              class="truncate"
              :title="documentable.name"
            >
              {{ documentable.name }}
            </span>
            <span
              v-else-if="documentable.type === 'User'"
              class="truncate"
              :title="documentable.available_name"
            >
              {{ documentable.available_name }}
            </span>
            <span
              v-else-if="documentable.type === 'Conversation'"
              class="hover:underline truncate cursor-pointer"
              role="button"
              @click="handleDocumentableClick"
            >
              {{
                t(`CAPTAIN.RESPONSES.DOCUMENTABLE.CONVERSATION`, {
                  id: documentable.display_id,
                })
              }}
            </span>
          </div>
        </div>
        <div
          class="shrink-0 text-sm text-n-slate-11 line-clamp-1 inline-flex items-center gap-1.5"
          :title="embeddingStatusLabel"
        >
          <Icon icon="i-ph-calendar-dot" class="size-3.5" />
          {{ timestamp }}
          <span
            class="size-1.5 rounded-full shrink-0"
            :class="embeddingStatusDotClass"
          />
        </div>
      </div>
    </div>
  </CardLayout>
</template>
